import React, { useState } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { css } from '/imports/ui/css';
import { strings } from '/imports/strings';
import { localStorageGet, localStorageSet } from '/imports/utils';
import { toastOnError } from '/imports/ui/utils';
import { Button } from '/imports/ui/Button';
import { DevToolSection } from '/imports/ui/DevToolSection';
import { FormField } from '/imports/ui/FormField';
import { Input } from '/imports/ui/Input';
import { Select } from '/imports/ui/Select';
import { quickbooksCashSync } from '/imports/api/quickbooks';

const DevToolQuickbooksCashSyncStyled = styled.div`
  .field { margin-bottom: 0.75rem; max-width: 46rem; }
  label { display: block; margin-bottom: 0.25rem; font-weight: 500; }
  .field .input, .field input, .field select { width: 100%; box-sizing: border-box; }
  .meta-row { color: ${css.grey3}; font-size: 0.8rem; margin-top: 0.25rem; }
  .buttons { display: flex; gap: 0.5rem; margin: 0.5rem 0 0.75rem; }
  .totals { display: flex; gap: 1rem; flex-wrap: wrap; margin-bottom: 0.5rem; font-weight: 500; }
  .changes { max-height: 24rem; overflow: auto; }
  table { width: 100%; border-collapse: collapse; font-size: 0.8rem; }
  th, td { text-align: left; padding: 0.25rem 0.5rem; border-bottom: 1px solid ${css.grey7}; }
  td.num { text-align: right; }
  tr.set td:first-child { border-left: 3px solid ${css.green}; }
  tr.noProject td:first-child, tr.noCashEvent td:first-child { border-left: 3px solid ${css.red2}; }
`;

const STORAGE_KEY = 'quickbooks.cashSync';
const environmentOptions = [
  { value: 'production', label: strings.Production() },
  { value: 'sandbox', label: strings.Sandbox() },
];

/** Dev tool: run the QuickBooks inflow sweep, dry or for real, and show every row it would touch. */
export const DevToolQuickbooksCashSync = () => {
  const [saved] = useState(() => {
    try {
      const value = typeof localStorage === 'undefined' ? null : localStorageGet(STORAGE_KEY);
      return value && typeof value === 'object' ? value as any : null;
    }
    catch (_) {
      return null;
    }
  });
  const [form, setForm] = useState({ accessToken: '', realmId: '', environment: 'production', ...saved });
  const [loading, setLoading] = useState('');
  const [result, setResult] = useState<any>();
  /**
   * Applies a change to the form and remembers it.
   * @param patch - the fields that changed
   */
  const set = (patch: any) => setForm((current: any) => {
    const next = { ...current, ...patch };
    try { localStorageSet(STORAGE_KEY, next); }
    catch (_) { /* the form still works, it just will not survive a reload */ }
    return next;
  });
  /**
   * Runs a sweep.
   * @param dryRun - report only, writing nothing
   */
  const run = (dryRun: boolean) => toastOnError(async () => {
    if (loading) return;
    setLoading(dryRun ? 'dry' : 'apply');
    setResult(undefined);
    try {
      const r = await quickbooksCashSync.call({
        dryRun,
        accessToken: form.accessToken || undefined,
        realmId: form.realmId || undefined,
        environment: form.environment,
      });
      setResult(r);
      if (!r.ok) toast.error(r.message || strings.Error());
      else if (!r.updated) toast.success(strings.NoChanges());
      else toast.success(`${r.updated} ${dryRun ? strings.DryRun() : strings.Saved()}`);
    }
    finally {
      setLoading('');
    }
  })();
  const money = (n?: number) => (n == null ? '—' : n.toLocaleString(undefined, { maximumFractionDigits: 2 }));
  return (
    <DevToolSection title={strings.QuickbooksCashSync()} description={strings.QuickbooksCashSyncHelp()}>
      <DevToolQuickbooksCashSyncStyled>
        <FormField field="accessToken">
          <label>{strings.AccessToken()}</label>
          <Input value={form.accessToken} type="password" onChange={(v) => set({ accessToken: v || '' })} />
          <div className="meta-row">{strings.QuickbooksApiTokenHint()}</div>
        </FormField>
        <FormField field="realmId">
          <label>{strings.CompanyId()}</label>
          <Input value={form.realmId} onChange={(v) => set({ realmId: v || '' })} />
        </FormField>
        <FormField field="environment">
          <label>{strings.Environment()}</label>
          <Select value={form.environment} options={environmentOptions} onChange={(v) => set({ environment: v })} />
        </FormField>
        <div className="buttons">
          <Button className="secondary" onClick={() => run(true)} disabled={!!loading}>
            {loading === 'dry' ? strings.Sending() : strings.DryRun()}
          </Button>
          <Button onClick={() => run(false)} disabled={!!loading}>
            {loading === 'apply' ? strings.Sending() : strings.Apply()}
          </Button>
        </div>
        {
          result ?
            <div>
              <div className="totals">
                <span>{result.invoicesScanned} invoices</span>
                <span>{result.disbursementInvoices} disbursements</span>
                <span>{result.updated} {result.dryRun ? strings.DryRun() : strings.Saved()}</span>
                <span>{result.unchanged} unchanged</span>
                <span>{result.skippedNoProject} no project</span>
                <span>{result.skippedNoCashEvent} no cash event</span>
              </div>
              <div className="changes">
                <table>
                  <thead>
                    <tr>
                      <th>RIC #</th><th>Project</th><th>Milestone</th><th>Invoice</th>
                      <th className="num">Before</th><th className="num">After</th><th>Paid</th><th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {
                      (result.changes || []).map((c: any, i: number) => (
                        <tr key={i} className={c.action}>
                          <td>{c.externalId}</td>
                          <td>{c.projectName}</td>
                          <td>{c.milestoneKey}</td>
                          <td>{c.docNumber}</td>
                          <td className="num">{money(c.before)}</td>
                          <td className="num">{money(c.after)}</td>
                          <td>{c.paidDate || '—'}</td>
                          <td>{c.action}</td>
                        </tr>
                      ))
                    }
                  </tbody>
                </table>
              </div>
            </div>
            :
            null
        }
      </DevToolQuickbooksCashSyncStyled>
    </DevToolSection>
  );
};
